import { clsx } from 'clsx';
import * as React from 'react';
import { mergeProps, useFocusable, useObjectRef } from 'react-aria';
import {
    Tooltip as RACTooltip,
    type TooltipProps as RACTooltipProps,
    type TooltipRenderProps as RACTooltipRenderProps,
    TooltipTrigger as RACTooltipTrigger,
    type TooltipTriggerComponentProps as RACTooltipTriggerComponentProps,
} from 'react-aria-components';

export type HoverCardRootProps = React.PropsWithChildren<{
    delay?: number;
    disabled?: boolean;
    isOpen?: RACTooltipTriggerComponentProps['isOpen'];
    onOpenChange?: RACTooltipTriggerComponentProps['onOpenChange'];
}>;

export function HoverCardRoot(props: HoverCardRootProps) {
    if (process.env.GITBOOK_PREDICTABLE_UI) {
        return React.Children.map(props.children, (child) => child);
    }

    return (
        <RACTooltipTrigger
            delay={props.delay ?? 300}
            isDisabled={props.disabled}
            closeDelay={50}
            isOpen={props.isOpen}
            onOpenChange={props.onOpenChange}
        >
            {props.children}
        </RACTooltipTrigger>
    );
}

export function HoverCardTrigger(props: { children: React.ReactElement }) {
    const { children } = props;
    const triggerRef = useObjectRef<HTMLElement>((children as any).ref);
    const { focusableProps } = useFocusable({}, triggerRef);

    return React.cloneElement(
        children,
        mergeProps({ ...focusableProps, tabIndex: 0, ref: triggerRef }, children.props)
    );
}

export const HoverCard = React.forwardRef(function HoverCard(
    props: React.PropsWithChildren<RACTooltipProps>,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const { className, children, ...otherProps } = props;
    const frozenChildrenRef = React.useRef(children);

    return (
        <RACTooltip
            ref={ref}
            offset={6}
            {...otherProps}
            className={(props) =>
                clsx(
                    'z-tooltip transition-opacity',
                    getHoverCardAnimationClassName(props),
                    className
                )
            }
        >
            {(values) => {
                // Freeze the children while the hovercard is animating.
                if (!values.isEntering && !values.isExiting) {
                    frozenChildrenRef.current = children;
                    return children;
                }

                return frozenChildrenRef.current;
            }}
        </RACTooltip>
    );
});

function getHoverCardAnimationClassName(props: RACTooltipRenderProps): string {
    return clsx(
        'fill-mode-forwards',
        props.placement &&
            {
                bottom: 'origin-top',
                top: 'origin-bottom',
                left: 'origin-right',
                right: 'origin-left',
                center: 'origin-center',
            }[props.placement],
        props.isEntering &&
            clsx(
                'fade-in animate-in',
                props.placement &&
                    {
                        bottom: 'slide-in-from-top-1',
                        top: 'slide-in-from-bottom-1',
                        left: 'slide-in-from-right-1',
                        right: 'slide-in-from-left-1',
                        center: '',
                    }[props.placement]
            ),
        props.isExiting &&
            clsx(
                'fade-out zoom-out-95 animate-out',
                props.placement &&
                    {
                        bottom: 'slide-out-to-top-1',
                        top: 'slide-out-to-bottom-1',
                        left: 'slide-out-to-right-1',
                        right: 'slide-out-to-left-1',
                        center: '',
                    }[props.placement]
            )
    );
}
